import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { TaskContext } from '@/types/task'

interface ContextPickerProps {
  value?: TaskContext | null
  onChange: (context: TaskContext | undefined) => void
  /**
   * 'sm' = 28px box, sized to a Today action-rail cell. 'md' (default) = 36px,
   * which is every other call site — inbox cards, the bulk action bar. Opt-in
   * so shrinking the rail can't quietly shrink them too.
   */
  size?: 'sm' | 'md'
  /** Name the life area beside a dot instead of the bare tag icon — for a
   *  detail pane's labeled Area row, where an icon alone said nothing. */
  showLabel?: boolean
  /** With showLabel and nothing chosen: what the button says (default
   *  "Not set"; a write line says "Space"). */
  emptyLabel?: string
}

const CONTEXTS: { value: TaskContext; label: string; color: string }[] = [
  { value: 'work', label: 'Work', color: 'rgb(37 99 235)' },      // Blue-600 (matches domain switcher)
  { value: 'family', label: 'Family', color: 'rgb(217 119 6)' },  // Amber-600 (matches domain switcher)
  { value: 'personal', label: 'Personal', color: 'rgb(147 51 234)' }, // Purple-600 (matches domain switcher)
]

/** The menu's widest likely width (min-w-[120px] plus its padding). */
const MENU_WIDTH = 136

export function ContextPicker({ value, onChange, size = 'md', showLabel = false, emptyLabel = 'Not set' }: ContextPickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{ top?: number; bottom?: number; right: number }>({ top: 0, right: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Calculate menu position when opening. Flip the menu ABOVE the trigger when
  // there isn't room below (e.g. this picker lives in the bottom-fixed bulk
  // action bar, where opening downward gets clipped by the viewport edge).
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const openUp = spaceBelow < 220
      setMenuPosition({
        top: openUp ? undefined : rect.bottom + 4,
        bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
        // Never off the left edge (a Week row in the first column).
        right: Math.min(window.innerWidth - rect.right, Math.max(8, window.innerWidth - MENU_WIDTH - 8)),
      })
    }
  }, [isOpen])

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      const target = event.target as Node
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('touchstart', handleClickOutside)
      }
    }
  }, [isOpen])

  const handleSelect = (ctx: TaskContext | undefined) => {
    onChange(ctx)
    setIsOpen(false)
  }

  const hasValue = value != null
  const selectedContext = CONTEXTS.find(ctx => ctx.value === value)

  // Map context colors to match domain switcher
  const contextStyles = {
    work: { color: 'rgb(37 99 235)' },      // Blue-600
    family: { color: 'rgb(217 119 6)' },    // Amber-600
    personal: { color: 'rgb(147 51 234)' }, // Purple-600
  }

  const isSmall = size === 'sm'
  const padClass = isSmall ? 'p-1.5' : 'p-2'
  const iconClass = isSmall ? 'w-4 h-4' : 'w-5 h-5'

  const menuContent = isOpen ? (
    <div
      ref={menuRef}
      onClick={(e) => e.stopPropagation()}
      className="fixed z-[9999] bg-white rounded-xl border border-neutral-200 shadow-lg p-2 min-w-[120px] animate-fade-in-up"
      style={{
        top: menuPosition.top,
        bottom: menuPosition.bottom,
        right: menuPosition.right,
      }}
    >
      <div className="space-y-1">
        {CONTEXTS.map(({ value: ctxValue, label, color }) => (
          <button
            key={ctxValue}
            type="button"
            onClick={() => handleSelect(ctxValue)}
            className={`w-full px-3 py-1.5 text-sm text-left rounded-lg flex items-center gap-2 ${
              value === ctxValue
                ? 'bg-primary-50 text-primary-700'
                : 'hover:bg-neutral-50 text-neutral-700'
            }`}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            {label}
          </button>
        ))}
        {hasValue && (
          <>
            <div className="border-t border-neutral-100 my-1" />
            <button
              type="button"
              onClick={() => handleSelect(undefined)}
              className="w-full px-3 py-1.5 text-sm text-left rounded-lg hover:bg-red-50 text-red-600"
            >
              Clear
            </button>
          </>
        )}
      </div>
    </div>
  ) : null

  return (
    <div ref={triggerRef} className="relative">
      {/* type="button": inside an add row's form, a default (submit) button
          is the one Enter "clicks" — the year's add row opened this menu
          on every Enter (walkthrough 2026-10-02, #4). */}
      {showLabel ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen) }}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-label={selectedContext ? `Space: ${selectedContext.label}. Change` : 'Set space'}
          className={`-ml-1.5 inline-flex min-h-[30px] items-center gap-2 rounded-md px-1.5 text-[14px] transition-colors hover:bg-neutral-100 ${selectedContext ? 'text-neutral-800' : 'text-neutral-500 tag-needs-context'}`}
        >
          <span
            aria-hidden
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={selectedContext ? { backgroundColor: selectedContext.color } : { boxShadow: 'inset 0 0 0 1.5px rgb(163 163 163)' }}
          />
          {selectedContext ? selectedContext.label : emptyLabel}
        </button>
      ) : (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen) }}
          className={`${padClass} rounded-lg transition-colors hover:bg-neutral-100 ${hasValue ? '' : 'tag-needs-context'}`}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-label="Set context"
          // The tag names its life area on hover (walkthrough 2026-10-02: an
          // unlabeled mark in task details).
          title={selectedContext ? `Space: ${selectedContext.label}` : hasValue ? undefined : 'Untagged — tap to set Work / Family / Personal'}
        >
          <svg
            className={`${iconClass} transition-colors`}
            fill={selectedContext ? 'currentColor' : 'none'}
            stroke={selectedContext ? undefined : 'currentColor'}
            strokeWidth={selectedContext ? undefined : 2}
            viewBox="0 0 24 24"
            style={selectedContext ? contextStyles[selectedContext.value as keyof typeof contextStyles] : { color: 'rgb(163 163 163)' }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        </button>
      )}
      {menuContent && createPortal(menuContent, document.body)}
    </div>
  )
}
